import React, { useId, useMemo, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import classNames from 'classnames';

interface LabelProps {
    text: string;
    children: React.ReactElement;
    className?: string;
    inline?: boolean;
    description?: string;
    required?: boolean | { required: boolean; message: string };
}

const Label = ({ text, children, className, inline, description, required = false }: LabelProps) => {
    const id = useId();
    const isRequired = typeof required === 'boolean' ? required : required?.required;
    const requiredMessage = typeof required === 'object' ? required.message : 'This field is required';
    const [isInvalid, setIsInvalid] = useState(false);

    const Child = useMemo(() => {
        const _child = children as any as React.ReactElement;
        const childProps = _child.props as any;
        const cloned = React.cloneElement(_child, {
            id,
            onInvalid: (e: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                setIsInvalid(true);
                childProps.onInvalid?.(e);
            },
            onInput: (e: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                const target = e.currentTarget;
                setIsInvalid(!target.validity.valid);
                childProps.onInput?.(e);
            }
        } as any);
        return cloned;
    }, [children]);

    return (
        <div className={classNames({
            "flex items-center gap-1": inline,
            "flex flex-col gap-1": !inline
        })}>
            <div>
                <label className={twMerge(classNames("font-roboto text-[14px] text-[#2d3a3f] font-medium text-sm ", { "flex-shrink-0": inline }), className)} htmlFor={id}>
                    {text}
                    {isRequired && <span className="text-[#2d3a3f] ml-1">*</span>}
                </label>
            </div>
            {Child}
            {description && <p className="text-[12px] text-[#5a717a]">{description}</p>}
            {isRequired && isInvalid && <p className="text-[12px] text-red-500">{requiredMessage}</p>}
        </div>
    );
};

export { Label };
